import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Alert from '@mui/material/Alert';
import { useSession } from '../context/SessionContext';
import { getSession, joinSession } from '../utils/api';

const JoinSession = () => {
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession, setParticipant } = useSession();
  
  // Get sessionId from URL params or query string (for invitation links)
  const searchParams = new URLSearchParams(location.search);
  const querySessionId = searchParams.get('sid');
  
  // Use whichever sessionId is available (url param or query string)
  const sessionId = urlSessionId || querySessionId;
  
  console.log('JoinSession component rendered with:', {
    urlSessionId,
    querySessionId,
    finalSessionId: sessionId,
    location: location.pathname,
    search: location.search
  });
  
  const [formData, setFormData] = useState({
    name: '',
    role: 'participant'
  });
  const [sessionData, setSessionData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  
  const { name, role } = formData;
  
  useEffect(() => {
    // On component mount, fetch the session data
    if (!sessionId) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      try {
        console.log('Fetching session data for session ID:', sessionId);
        const data = await getSession(sessionId);
        console.log('Received session data:', data);
        setSessionData(data);
      } catch (err) {
        console.error('Error fetching session:', err);
        setError('Session not found or unavailable');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [sessionId]);
  
  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const onSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    
    try {
      setJoining(true);
      setError(null);
      
      console.log('Joining session with data:', { sessionId, name: name.trim(), role });
      
      const participant = await joinSession(sessionId, name.trim(), role);
      
      console.log('Received participant data:', participant);
      
      // Ensure both id and _id are set for compatibility
      if (participant.id && !participant._id) {
        participant._id = participant.id;
      } else if (participant._id && !participant.id) {
        participant.id = participant._id;
      }
      
      // Set data in context (will be saved to localStorage)
      setSession(sessionData);
      setParticipant(participant);
      
      // Navigate to the meeting path
      navigate(`/meeting/${sessionId}`);
      
    } catch (err) {
      console.error('Error joining session:', err);
      
      // If the error is because the user already exists in the session,
      // we should allow them to reconnect
      if (err.response && err.response.status === 400 && 
          err.response.data && err.response.data.msg && 
          err.response.data.msg.includes('already in use in this session')) {
        setError('This name is already in use. If this is you, try reconnecting:');
        // Add button to reconnect will be shown via conditional rendering
      } else {
        setError(err.response?.data?.msg || 'Error joining session');
      }
      
      setJoining(false);
    }
  };
  
  // Handle reconnection attempt
  const handleReconnect = async () => {
    try {
      setJoining(true);
      
      // Use the current origin or fallback to development URL
      const baseUrl = process.env.NODE_ENV === 'production' 
        ? window.location.origin
        : (window.location.hostname === 'localhost' ? `http://${window.location.hostname}:5000` : window.location.origin);
      
      // First, get the existing participant data
      const response = await fetch(`${baseUrl}/api/participants/by-name`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          name: name.trim()
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to reconnect');
      }
      
      const participant = await response.json();
      
      // Ensure both id and _id are set for compatibility
      if (participant.id && !participant._id) {
        participant._id = participant.id;
      } else if (participant._id && !participant.id) {
        participant.id = participant._id;
      }
      
      setSession(sessionData);
      setParticipant(participant);
      
      // Navigate to the meeting path
      navigate(`/meeting/${sessionId}`);
      
    } catch (err) {
      console.error('Error reconnecting:', err);
      setError('Failed to reconnect. Please try with a different name.');
      setJoining(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!sessionData) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">
          {error || 'Session not found. Please check the URL and try again.'}
        </Alert>
      </Box>
    );
  }
  
  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} md={8} lg={6}>
        <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Join Session
          </Typography>
          
          <Typography variant="h6" align="center" gutterBottom>
            {sessionData.name}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Created by: {sessionData.createdBy}
          </Typography>
          
          <Box component="form" onSubmit={onSubmit} noValidate sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Your Name"
                  fullWidth
                  value={name}
                  onChange={onChange}
                  required
                  autoFocus
                  error={error && !name.trim()}
                  helperText={error && !name.trim() ? 'Your name is required' : ''}
                  disabled={joining}
                  placeholder="e.g., John Doe"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Typography variant="body2" gutterBottom>
                    Join as:
                  </Typography>
                  <RadioGroup
                    aria-label="role"
                    name="role"
                    value={role}
                    onChange={onChange}
                    row
                  >
                    <FormControlLabel
                      value="participant"
                      control={<Radio />}
                      label="Participant (can vote)"
                      disabled={joining}
                    />
                    <FormControlLabel
                      value="observer"
                      control={<Radio />}
                      label="Observer (view only)"
                      disabled={joining}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              
              {error && error !== 'Name is required' && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                  
                  {error.includes('already in use') && (
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onClick={handleReconnect}
                      disabled={joining}
                      sx={{ mt: 2 }}
                    >
                      Reconnect as {name.trim()}
                    </Button>
                  )}
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={joining}
                  sx={{ mt: 2 }}
                >
                  {joining ? 'Joining...' : 'Join Session'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default JoinSession; 
